import { useContext, useEffect, useState, type PropsWithChildren } from "react";

import { reloadAfterIdentityChange } from "@/lib/reloadAfterIdentityChange";
import { AuthContext } from "@/shared/context/auth-context";

/**
 * Restarts the app in place when the account this tab has been showing is signed out or replaced
 * — from another tab, by expiry, or by an in-page switch — so nothing keeps rendering or acting on
 * its cached data. Signing in from anonymous keeps the page: nothing private was cached, and the
 * sign-in flows navigate on their own.
 *
 * It acts on the same settled answers as `AuthAnalyticsBridge`, which `Providers` mounts first, so
 * the bridge's owner cleanup has run by the time this reloads.
 */
export const AuthIdentityBoundary = ({ children }: PropsWithChildren) => {
  const { session, isFetching, isError } = useContext(AuthContext);
  // The impersonator counts: starting or ending an impersonation changes whose data is cached.
  const identity = session ? JSON.stringify([session.user.id, session.user.spoofingId ?? null]) : null;
  const [shownIdentity, setShownIdentity] = useState<string | null>(null);
  const [replaced, setReplaced] = useState(false);

  // Updating state during render hides the children in this same pass: no commit renders them as
  // the new identity over the old cache, and their beforeunload guards are gone before the reload.
  if (!isFetching && !isError && !replaced && identity !== shownIdentity) {
    if (shownIdentity === null) setShownIdentity(identity);
    else setReplaced(true);
  }

  useEffect(() => {
    if (replaced) reloadAfterIdentityChange();
  }, [replaced]);

  return replaced ? null : children;
};
